import { useEffect, useRef, useState } from "react";
import type { PlayerRef } from "@remotion/player";
import type { CompositionDto } from "@repo/api/editor";
import { getAllElements, getResolution } from "@repo/api/utils/composition";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useCanvasStore } from "@/lib/stores/canvas-store/canvas-store";
import { isActiveElementInput } from "@/lib/utils";
import {
  clientToComposition,
  Point,
  getBoundingBox,
  isSameBox,
} from "./canvas-geometry";
import { getSelectedBoxes } from "@/lib/stores/canvas-store/canvas-selection";
import type { ResizeHandle } from "@/lib/stores/canvas-store/canvas-resize";
import SelectionOutline from "./SelectionOutline";
import SelectionMarquee from "./SelectionMarquee";
import Guideline from "./Guideline";
import ResizeHandles, { RESIZE_CURSORS } from "./ResizeHandles";
import ActionBar from "./ActionBar";
import TextEditor from "./TextEditor";
import { matchesShortcut, ORDERING_SHORTCUTS } from "./shortcuts";
import type { ElementOrdering } from "@/lib/stores/canvas-store/canvas-layer-order";
import {
  ALIGN_SHORTCUT_KEYS,
  AlignType,
} from "@/lib/stores/canvas-store/canvas-align";
import { VIDEO_PREVIEW_CONTAINER_ID } from "../VideoPreview/VideoPreview";
import { ASPECT_RATIO_CONTROLS_ID } from "../VideoPreview/AspectRatioControls";
import { VIDEO_PREVIEW_CONTROLS_ID } from "../VideoPreview/VideoPreviewControls/VideoPreviewControls";

function isCanvasTarget(target: Element) {
  return (
    target.closest(`#${VIDEO_PREVIEW_CONTAINER_ID}`) !== null &&
    target.closest(`#${ASPECT_RATIO_CONTROLS_ID}`) === null &&
    target.closest(`#${VIDEO_PREVIEW_CONTROLS_ID}`) === null
  );
}

type Props = {
  composition: CompositionDto;
  playerRef: React.RefObject<PlayerRef | null>;
};

// Areas like the elements list and the action bar don't clear the selection or start a press.
function isKeepSelectionTarget(target: Element) {
  return target.closest("[data-keep-selection]") !== null;
}

function getResizeHandle(target: Element) {
  return (target
    .closest("[data-resize-handle]")
    ?.getAttribute("data-resize-handle") ?? null) as ResizeHandle | null;
}

const MIN_SIZE_FOR_EDGE_PILLS_SCREEN_PX = 50;

const ALIGN_TYPES = Object.keys(ALIGN_SHORTCUT_KEYS) as AlignType[];
const ORDERINGS = Object.keys(ORDERING_SHORTCUTS) as ElementOrdering[];

const ARROW_DIRECTIONS: Record<string, Point> = {
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
};

export default function CanvasLayer({ composition, playerRef }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  const canvasService = useCanvasStore((state) => state.service);
  const canvasElements = useCanvasStore((state) => state.canvasElements);
  const marquee = useCanvasStore((state) => state.marquee);
  const drafts = useCanvasStore((state) => state.drafts);
  const guidelines = useCanvasStore((state) => state.guidelines);
  const selectedElementIds = useEditorStore(
    (state) => state.selectedElementIds,
  );

  const resolution = getResolution(composition.aspectRatio);
  const [scale, setScale] = useState(1);

  const selectionIncludesText = useEditorStore((state) =>
    getAllElements(state.video?.compositionJson).some(
      ({ element }) =>
        element.type === "TEXT" &&
        state.selectedElementIds.includes(element.externalId),
    ),
  );

  useEffect(() => {
    const canvas = ref.current;

    if (!canvas) {
      return;
    }

    const observer = new ResizeObserver(() => {
      setScale(canvas.getBoundingClientRect().width / resolution.width);
    });

    observer.observe(canvas);

    return () => {
      observer.disconnect();
    };
  }, [resolution.width]);

  useEffect(() => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    const handleFrameUpdate = () => {
      canvasService.setFrame(player.getCurrentFrame());
    };

    handleFrameUpdate();
    player.addEventListener("frameupdate", handleFrameUpdate);

    return () => {
      player.removeEventListener("frameupdate", handleFrameUpdate);
    };
  }, [playerRef, canvasService]);

  useEffect(() => {
    const getCanvasRect = () => ref.current?.getBoundingClientRect();

    const toCompositionPoint = (event: MouseEvent, canvasRect: DOMRect) =>
      clientToComposition(
        { x: event.clientX, y: event.clientY },
        canvasRect,
        resolution,
      );

    const handlePointerDown = (event: PointerEvent) => {
      if (
        !event.isPrimary ||
        event.button !== 0 ||
        !(event.target instanceof Element)
      ) {
        return;
      }

      if (isKeepSelectionTarget(event.target)) {
        return;
      }

      if (!isCanvasTarget(event.target)) {
        canvasService.clearSelection();
        return;
      }

      const canvasRect = getCanvasRect();

      if (canvasRect) {
        event.target.setPointerCapture(event.pointerId);
        canvasService.pointerDown(
          toCompositionPoint(event, canvasRect),
          { shift: event.shiftKey },
          canvasRect.width / resolution.width,
          getResizeHandle(event.target),
        );
      }
    };

    const handlePointerMove = (event: PointerEvent) => {
      const canvasRect = getCanvasRect();

      if (!event.isPrimary || !canvasRect) {
        return;
      }

      const point = toCompositionPoint(event, canvasRect);
      const isOverCanvas =
        event.target instanceof Element &&
        isCanvasTarget(event.target) &&
        !isKeepSelectionTarget(event.target) &&
        !getResizeHandle(event.target);

      canvasService.pointerMove(point, { shift: event.shiftKey });
      canvasService.hover(isOverCanvas ? point : null);
    };

    // Not checking the button: with another button pressed during a drag, the
    // pointerup comes only after the last button is released.
    const handlePointerUp = (event: PointerEvent) => {
      if (event.isPrimary) {
        canvasService.pointerUp({ shift: event.shiftKey });
      }
    };

    const handleShiftKey = (event: KeyboardEvent) => {
      if (event.key === "Shift" && !event.repeat) {
        canvasService.modifiersChange({ shift: event.shiftKey });
      }
    };

    const handleArrowKey = (event: KeyboardEvent) => {
      const direction = ARROW_DIRECTIONS[event.key];

      if (
        !direction ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isActiveElementInput()
      ) {
        return;
      }

      const step = event.shiftKey ? 10 : 1;

      if (
        canvasService.nudgeSelection(direction.x * step, direction.y * step)
      ) {
        event.preventDefault();
      }
    };

    const handleShortcutKey = (event: KeyboardEvent) => {
      // Already handled, e.g. Esc closing a menu or dialog.
      if (event.defaultPrevented || isActiveElementInput()) {
        return;
      }

      const key = event.key.toLowerCase();
      const isMod = event.metaKey || event.ctrlKey;
      const isPlainMod = isMod && !event.shiftKey && !event.altKey;
      const ordering = ORDERINGS.find((ordering) =>
        matchesShortcut(event, ORDERING_SHORTCUTS[ordering]),
      );
      let isHandled = false;

      if (key === "backspace" || key === "delete") {
        isHandled = canvasService.deleteSelection();
      } else if (!isMod && key === "escape") {
        isHandled = canvasService.escape();
      } else if (!isMod && key === "enter") {
        isHandled = canvasService.editSelectedText();
      } else if (isPlainMod && key === "a") {
        isHandled = canvasService.selectAll();
      } else if (isPlainMod && key === "h") {
        isHandled = canvasService.toggleSelectionVisibility();
      } else if (isPlainMod && key === "d") {
        isHandled = canvasService.duplicateSelection();
      } else if (
        isPlainMod &&
        key === "c" &&
        // Normal text copy wins when text is selected on the page.
        !window.getSelection()?.toString()
      ) {
        isHandled = canvasService.copySelection();
      } else if (isPlainMod && key === "v") {
        isHandled = canvasService.pasteElements();
      } else if (ordering) {
        isHandled = canvasService.reorderSelection(ordering);
      } else if (event.altKey && !isMod && !event.shiftKey) {
        // event.code, because Option + letter types a special character on Mac.
        const alignType = ALIGN_TYPES.find(
          (alignType) => event.code === `Key${ALIGN_SHORTCUT_KEYS[alignType]}`,
        );

        if (alignType) {
          isHandled = canvasService.alignSelection(alignType);
        }
      }

      if (isHandled) {
        event.preventDefault();
      }
    };

    const handleDoubleClick = (event: MouseEvent) => {
      const canvasRect = getCanvasRect();

      if (
        canvasRect &&
        event.target instanceof Element &&
        isCanvasTarget(event.target) &&
        !isKeepSelectionTarget(event.target) &&
        !getResizeHandle(event.target)
      ) {
        canvasService.doubleClick(toCompositionPoint(event, canvasRect));
      }
    };

    const handlePointerLeave = () => {
      canvasService.hover(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
    document.addEventListener("dblclick", handleDoubleClick);
    document.addEventListener("pointercancel", canvasService.cancelGesture);
    document.addEventListener("keydown", handleShiftKey);
    document.addEventListener("keyup", handleShiftKey);
    document.addEventListener("keydown", handleArrowKey);
    document.addEventListener("keydown", handleShortcutKey);
    window.addEventListener("blur", canvasService.cancelGesture);
    document.documentElement.addEventListener(
      "pointerleave",
      handlePointerLeave,
    );

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      document.removeEventListener("dblclick", handleDoubleClick);
      document.removeEventListener(
        "pointercancel",
        canvasService.cancelGesture,
      );
      document.removeEventListener("keydown", handleShiftKey);
      document.removeEventListener("keyup", handleShiftKey);
      document.removeEventListener("keydown", handleArrowKey);
      document.removeEventListener("keydown", handleShortcutKey);
      window.removeEventListener("blur", canvasService.cancelGesture);
      document.documentElement.removeEventListener(
        "pointerleave",
        handlePointerLeave,
      );
      canvasService.cancelGesture();
      canvasService.commitNudge();
    };
  }, [canvasService, resolution]);

  const selectedBoxes = getSelectedBoxes(
    canvasElements,
    selectedElementIds,
    drafts,
  );
  const selectionBox = getBoundingBox(selectedBoxes);
  const isMultipleSelected = selectedBoxes.length > 1;
  const activeHandle = useCanvasStore((state) => state.activeHandle);
  const hoveredBox = useCanvasStore(
    (state) =>
      state.canvasElements.find(
        ({ externalId }) =>
          externalId === state.hoveredId &&
          !selectedElementIds.includes(externalId),
      )?.box,
  );
  const editingTextId = useCanvasStore((state) => state.editingTextId);
  const isEditingText = editingTextId !== null;
  // Typing also creates a draft, but that isn't moving.
  const isMoving =
    Object.keys(drafts).length > 0 && activeHandle === null && !isEditingText;
  const editingText = useEditorStore(
    (state) =>
      getAllElements(state.video?.compositionJson).find(
        ({ element }) => element.externalId === editingTextId,
      )?.element,
  );
  const editingTextBox =
    (editingTextId && drafts[editingTextId]) ??
    canvasElements.find(({ externalId }) => externalId === editingTextId)?.box;
  const cursor = activeHandle ? RESIZE_CURSORS[activeHandle] : null;

  // Set on the body so the resize cursor stays while dragging off the handle.
  useEffect(() => {
    if (!cursor) {
      return;
    }

    document.body.style.cursor = cursor;

    return () => {
      document.body.style.cursor = "";
    };
  }, [cursor]);

  return (
    <div ref={ref} className="pointer-events-none absolute inset-0 z-10">
      {isMultipleSelected &&
        !isMoving &&
        selectedBoxes
          .filter((box) => selectionBox && !isSameBox(box, selectionBox))
          .map((box, index) => (
            <SelectionOutline key={index} box={box} resolution={resolution} />
          ))}
      {selectionBox && !isMoving && (
        <SelectionOutline
          box={selectionBox}
          resolution={resolution}
          isDashed={isMultipleSelected}
        />
      )}
      {selectionBox && !isMoving && !isEditingText && (
        <ResizeHandles
          box={selectionBox}
          resolution={resolution}
          hasEdges={!selectionIncludesText}
          activeHandle={activeHandle}
          showEdgePills={
            selectionBox.w * scale > MIN_SIZE_FOR_EDGE_PILLS_SCREEN_PX &&
            selectionBox.h * scale > MIN_SIZE_FOR_EDGE_PILLS_SCREEN_PX
          }
        />
      )}
      {selectionBox && !isMoving && activeHandle === null && !isEditingText && (
        <ActionBar box={selectionBox} resolution={resolution} />
      )}
      {hoveredBox && (
        <SelectionOutline box={hoveredBox} resolution={resolution} />
      )}
      {editingText?.type === "TEXT" && editingTextBox && (
        <TextEditor
          key={editingText.externalId}
          element={editingText}
          box={editingTextBox}
          resolution={resolution}
          scale={scale}
        />
      )}
      {marquee && <SelectionMarquee box={marquee} resolution={resolution} />}
      {guidelines.map((guideline) => (
        <Guideline
          key={`${guideline.orientation}-${guideline.position}`}
          guideline={guideline}
          resolution={resolution}
        />
      ))}
    </div>
  );
}
